import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Send, Volume2, VolumeX, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import { API, api, authHeader } from "../lib/api";
import { TutorAvatar } from "../components/TutorAvatar";

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [autoVoice, setAutoVoice] = useState(true);
  const bottomRef = useRef(null);
  const audioRef = useRef(null);
  const photoRef = useRef(null);
  const location = useLocation();

  const sendPhoto = async (file) => {
    if (!file || streaming) return;
    const caption = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "student", content: `📷 Sent a photo of homework${caption ? `: "${caption}"` : ""}` }, { role: "mia", content: "" }]);
    setStreaming(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("question", caption);
      const { data } = await api.post("/homework-help", fd, { timeout: 90000 });
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "mia", content: data.reply };
        return copy;
      });
      if (autoVoice) playTTS(data.reply);
    } catch (e) {
      const detail = e.response?.data?.detail || "Mia couldn't read that photo — try again?";
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "mia", content: detail };
        return copy;
      });
      toast.error(detail);
    }
    setStreaming(false);
  };

  useEffect(() => {
    if (location.state?.prefill) setInput(location.state.prefill);
  }, [location.state]);

  useEffect(() => {
    api.get("/chat/history").then((r) => setMessages(r.data));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const playTTS = async (text) => {
    try {
      audioRef.current?.pause();
      const r = await api.post("/tts", { text });
      const audio = new Audio(`data:audio/mp3;base64,${r.data.audio_base64}`);
      audioRef.current = audio;
      setSpeaking(true);
      audio.onended = () => setSpeaking(false);
      audio.onerror = () => setSpeaking(false);
      await audio.play();
    } catch {
      setSpeaking(false);
    }
  };

  const stopTTS = () => {
    audioRef.current?.pause();
    setSpeaking(false);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "student", content: text }, { role: "mia", content: "" }]);
    setStreaming(true);
    let full = "";
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader() },
        body: JSON.stringify({ message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.delta) {
            full += ev.delta;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "mia", content: full };
              return copy;
            });
          }
          if (ev.error) full += " (Hmm, something glitched — try again?)";
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "mia", content: "Oops, I lost connection. Try that again?" };
        return copy;
      });
    }
    setStreaming(false);
    if (full && autoVoice) playTTS(full);
  };

  return (
    <div className="max-w-lg mx-auto flex flex-col h-screen">
      <header className="sticky top-0 z-40 bg-[#FAFAFA] border-b-2 border-black px-5 py-3 flex items-center gap-4">
        <TutorAvatar speaking={speaking} size="w-14 h-14" />
        <div className="flex-1">
          <h1 className="font-heading text-xl font-semibold">Mia</h1>
          <p className="text-xs text-gray-600">{speaking ? "Speaking..." : streaming ? "Typing..." : "Your ADHD-savvy tutor"}</p>
        </div>
        <button
          onClick={() => (speaking ? stopTTS() : setAutoVoice(!autoVoice))}
          data-testid="voice-toggle-button"
          className={`p-2.5 rounded-full border-2 border-black shadow-[2px_2px_0px_rgba(0,0,0,1)] transition-all ${autoVoice ? "bg-[#F2C6DE]" : "bg-white"}`}
          title={autoVoice ? "Voice on" : "Voice off"}
        >
          {autoVoice ? <Volume2 size={18} /> : <VolumeX size={18} />}
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 pb-40" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6" data-testid="chat-welcome">
            <p className="font-bold mb-1">Hey! I'm Mia 🌸</p>
            <p className="text-sm text-gray-700">Your tutor, coach and hype-girl. Ask me anything — homework help, how to survive a teacher you can't stand, or just tell me how your day went.</p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={m.ts || `local-${i}`} className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[85%] px-4 py-3 border-2 border-black rounded-2xl shadow-[3px_3px_0px_rgba(0,0,0,1)] text-sm whitespace-pre-wrap ${
                m.role === "student" ? "bg-[#4D96FF] rounded-br-sm" : "bg-white rounded-bl-sm"
              }`}
              data-testid={`chat-msg-${m.role}-${i}`}
            >
              {m.content || <Loader2 size={16} className="animate-spin" />}
              {m.role === "mia" && m.content && (
                <button
                  onClick={() => playTTS(m.content)}
                  data-testid={`play-tts-${i}`}
                  className="block mt-2 text-gray-500 hover:text-black transition-colors"
                >
                  <Volume2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="fixed bottom-[72px] left-0 right-0 bg-[#FAFAFA] border-t-2 border-black px-4 py-3">
        <div className="max-w-lg mx-auto flex gap-2">
          <input
            ref={photoRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            data-testid="homework-photo-input"
            onChange={(e) => {
              sendPhoto(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => photoRef.current?.click()}
            disabled={streaming}
            data-testid="homework-photo-button"
            title="Snap a photo of a tricky question"
            className="bg-[#F2C6DE] border-2 border-black rounded-xl px-3.5 shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all disabled:opacity-50"
          >
            <Camera size={20} />
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask Mia anything..."
            data-testid="chat-input"
            className="flex-1 bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base"
          />
          <button
            onClick={send}
            disabled={streaming}
            data-testid="chat-send-button"
            className="bg-[#FFE066] border-2 border-black rounded-xl px-4 shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all disabled:opacity-50"
          >
            {streaming ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
          </button>
        </div>
      </div>
    </div>
  );
}
